import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { calculateDropdownPosition, closeAllDropdowns, CLOSE_ALL_DROPDOWNS_EVENT, FLOWCHART_PAN_START_EVENT } from '../../shared/utils/dropdownPositioning';
import {
  FilterIcon,
  CheckIcon,
  ChevronIcon,
  PRIORITY_OPTIONS,
  DIFFICULTY_OPTIONS,
  DUE_DATE_OPTIONS,
  MISSION_TYPE_OPTIONS,
  matchesDueDateFilter,
  matchesMissionTypeFilter,
  matchesAllCustomFieldFilters,
  countCustomFieldFilters,
  getTaskCategories,
  getTaskTags,
} from '../../shared/filterUtils';

import { KanbanFilterSection, KanbanFilterOption } from './components/FilterSection';
import CustomFieldFilterSection from './components/CustomFieldFilterSection';
import { useFilterState } from './hooks/useFilterState';

/**
 * KanbanFilterDropdown Component
 * 
 * Dropdown for filtering Kanban cards by:
 * - Mission type (available/active)
 * - Status (using actual statuses from the sprint)
 * - Category
 * - Tags
 * - Due Date
 * - Priority
 */
const KanbanFilterDropdown = ({
  filters = {},
  onFiltersChange,
  statuses = [],
  categories = [],
  allTags = [],
  tasks = [],
  showNestedCampaigns = true,
  onShowNestedCampaignsChange,
  lockStates = {},
  nestedSprints = [],
  showActionableOnly = false,
  onToggleActionableOnly,
  customFields = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Use custom hook for filter state management
  const {
    isHideCompletedActive,
    handleToggleHideCompleted,
    toggleFilter,
    toggleCustomFieldFilter,
    updateCustomFieldSearch,
    updateCustomFieldNumber,
    handleClearAll,
    customFieldFilterCount,
    activeFilterCount,
  } = useFilterState({
    filters,
    onFiltersChange,
    statuses,
    showActionableOnly,
  });

  // Calculate dropdown position with edge detection
  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition(calculateDropdownPosition(rect, 320));
    }
  }, []);

  // Open dropdown
  const handleToggle = useCallback(() => {
    if (!isOpen) {
      closeAllDropdowns(buttonRef.current);
      updatePosition();
    }
    setIsOpen(!isOpen);
  }, [isOpen, updatePosition]);

  // Listen for global close events
  useEffect(() => {
    const handleCloseAll = () => setIsOpen(false);
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    window.addEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    return () => {
      window.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
      window.removeEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Derived data
  const taskCategories = useMemo(() => getTaskCategories(tasks, categories), [tasks, categories]);
  const taskTags = useMemo(() => getTaskTags(tasks, allTags), [tasks, allTags]);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  }, []);

  const matchingCount = useMemo(() => {
    if (activeFilterCount === 0) return tasks.length;
    
    return tasks.filter(task => {
      if (filters.missionType?.length > 0) {
        if (!filters.missionType.some(filterId => matchesMissionTypeFilter(task, filterId, lockStates, nestedSprints, statuses))) return false;
      }
      if (filters.status?.length > 0 && !filters.status.includes(task.status)) return false;
      if (filters.category?.length > 0 && (!task.category || !filters.category.includes(task.category))) return false;
      if (filters.tags?.length > 0 && (!task.tags || !filters.tags.some(tag => task.tags.includes(tag)))) return false;
      if (filters.dueDate?.length > 0 && !filters.dueDate.some(filterId => matchesDueDateFilter(task.dueDate, filterId))) return false;
      if (filters.priority?.length > 0 && !filters.priority.includes(task.priority || 'none')) return false;
      if (filters.difficulty?.length > 0 && !filters.difficulty.includes(task.difficulty || 'none')) return false;
      if (filters.customFields && customFields.length > 0) {
        if (!matchesAllCustomFieldFilters(task, filters.customFields, customFields)) return false;
      }
      return true;
    }).length;
  }, [tasks, filters, activeFilterCount, lockStates, nestedSprints, statuses, customFields]);

  const isActive = activeFilterCount > 0;

  return (
    <>
      <button
        ref={buttonRef}
        className={`toolbar-btn ${isActive ? 'toolbar-btn--active' : ''}`}
        onClick={handleToggle}
        title="Filter cards"
      >
        <span className="toolbar-btn__spacer" />
        <span className="toolbar-btn__content">
          <span className="toolbar-btn__icon"><FilterIcon /></span>
          <span className="toolbar-btn__label">{isActive ? `Filter (${activeFilterCount})` : 'Filter'}</span>
        </span>
        <span className={`toolbar-btn__chevron ${isOpen ? 'toolbar-btn__chevron--open' : ''}`}><ChevronIcon /></span>
      </button>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="kanban-filter-dropdown__menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            maxHeight: dropdownPosition.maxHeight,
            overflowY: 'auto',
            zIndex: 50000,
          }}
        >
          <div className="kanban-filter-dropdown__header">
            <span className="kanban-filter-dropdown__title">Filter Cards</span>
            {activeFilterCount > 0 && <button className="kanban-filter-dropdown__clear" onClick={handleClearAll}>Clear all</button>}
          </div>

          {/* Quick Filters Section */}
          <div className="kanban-filter-dropdown__quick-section kanban-filter-dropdown__quick-section--compact">
            <label className="kanban-filter-dropdown__quick-option kanban-filter-dropdown__quick-option--compact">
              <span className="kanban-filter-dropdown__option-check">{showActionableOnly && <CheckIcon />}</span>
              <span className="kanban-filter-dropdown__option-label">Actionable Only</span>
              <span className="kanban-filter-dropdown__option-hint">Show only tasks you can work on now</span>
              <input type="checkbox" checked={showActionableOnly} onChange={() => onToggleActionableOnly?.()} className="kanban-filter-dropdown__option-input" />
            </label>
            <label className="kanban-filter-dropdown__quick-option kanban-filter-dropdown__quick-option--compact">
              <span className="kanban-filter-dropdown__option-check">{isHideCompletedActive && <CheckIcon />}</span>
              <span className="kanban-filter-dropdown__option-label">Hide Completed</span>
              <span className="kanban-filter-dropdown__option-hint">Filter to non-completed statuses only</span>
              <input type="checkbox" checked={isHideCompletedActive} onChange={handleToggleHideCompleted} className="kanban-filter-dropdown__option-input" />
            </label>
            {/* COMMENTED OUT: "Show Nested Campaigns" option removed
            <label className="kanban-filter-dropdown__quick-option kanban-filter-dropdown__quick-option--compact">
              <span className="kanban-filter-dropdown__option-check">{showNestedCampaigns && <CheckIcon />}</span>
              <span className="kanban-filter-dropdown__option-label">Show Nested Campaigns</span>
              <span className="kanban-filter-dropdown__option-hint">Include missions that have nested campaigns</span>
              <input type="checkbox" checked={showNestedCampaigns} onChange={(e) => onShowNestedCampaignsChange?.(e.target.checked)} className="kanban-filter-dropdown__option-input" />
            </label>
            */}
          </div>

          {/* Missions Section */}
          <KanbanFilterSection title="Missions" sectionId="missions" expandedSections={expandedSections} toggleSection={toggleSection} count={filters.missionType?.length}>
            {MISSION_TYPE_OPTIONS.map(option => {
              const IconComponent = option.icon;
              return (
                <label key={option.id} className="kanban-filter-dropdown__option">
                  <span className="kanban-filter-dropdown__option-check">{filters.missionType?.includes(option.id) && <CheckIcon />}</span>
                  <span className="kanban-filter-dropdown__option-icon" style={{ color: option.color }}><IconComponent /></span>
                  <span className="kanban-filter-dropdown__option-label">{option.label}</span>
                  <input type="checkbox" checked={filters.missionType?.includes(option.id) || false} onChange={() => toggleFilter('missionType', option.id)} className="kanban-filter-dropdown__option-input" />
                </label>
              );
            })}
          </KanbanFilterSection>

          {/* Status Section */}
          {statuses.length > 0 && (
            <KanbanFilterSection title="Status" sectionId="status" expandedSections={expandedSections} toggleSection={toggleSection} count={filters.status?.length}>
              {statuses.map(status => (
                <KanbanFilterOption key={status.id} label={status.name} color={status.color} checked={filters.status?.includes(status.id)} onChange={() => toggleFilter('status', status.id)} />
              ))}
            </KanbanFilterSection>
          )}

          {/* Primary Tag Section */}
          {taskCategories.length > 0 && (
            <KanbanFilterSection title="Primary Tag" sectionId="category" expandedSections={expandedSections} toggleSection={toggleSection} count={filters.category?.length}>
              {taskCategories.map(cat => (
                <KanbanFilterOption key={cat.name} label={cat.name} color={cat.color} checked={filters.category?.includes(cat.name)} onChange={() => toggleFilter('category', cat.name)} />
              ))}
            </KanbanFilterSection>
          )}

          {/* Tags Section */}
          {taskTags.length > 0 && (
            <KanbanFilterSection title="Tags" sectionId="tags" expandedSections={expandedSections} toggleSection={toggleSection} count={filters.tags?.length}>
              {taskTags.map(tag => (
                <KanbanFilterOption key={tag.name} label={tag.name} color={tag.color} checked={filters.tags?.includes(tag.name)} onChange={() => toggleFilter('tags', tag.name)} isTag />
              ))}
            </KanbanFilterSection>
          )}

          {/* Priority Section */}
          <KanbanFilterSection title="Priority" sectionId="priority" expandedSections={expandedSections} toggleSection={toggleSection} count={filters.priority?.length}>
            {PRIORITY_OPTIONS.map(option => (
              <KanbanFilterOption key={option.id} label={option.label} color={option.color} checked={filters.priority?.includes(option.id)} onChange={() => toggleFilter('priority', option.id)} />
            ))}
          </KanbanFilterSection>

          {/* Difficulty Section */}
          <KanbanFilterSection title="Difficulty" sectionId="difficulty" expandedSections={expandedSections} toggleSection={toggleSection} count={filters.difficulty?.length}>
            {DIFFICULTY_OPTIONS.map(option => (
              <KanbanFilterOption key={option.id} label={option.label} color={option.color} checked={filters.difficulty?.includes(option.id)} onChange={() => toggleFilter('difficulty', option.id)} />
            ))}
          </KanbanFilterSection>

          {/* Due Date Section */}
          <KanbanFilterSection title="Due Date" sectionId="dueDate" expandedSections={expandedSections} toggleSection={toggleSection} count={filters.dueDate?.length}>
            {DUE_DATE_OPTIONS.map(option => (
              <KanbanFilterOption key={option.id} label={option.label} color={option.color} checked={filters.dueDate?.includes(option.id)} onChange={() => toggleFilter('dueDate', option.id)} />
            ))}
          </KanbanFilterSection>

          {/* Custom Fields Section */}
          {customFields.length > 0 && customFields
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map(field => (
              <CustomFieldFilterSection
                key={field.id}
                field={field}
                filterCriteria={filters.customFields?.[field.id]}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                onToggleValue={(value) => toggleCustomFieldFilter(field.id, value)}
                onSearchChange={(search) => updateCustomFieldSearch(field.id, search)}
                onNumberChange={(operator, value) => updateCustomFieldNumber(field.id, operator, value)}
              />
            ))}

          <div className="kanban-filter-dropdown__footer">
            <span className="kanban-filter-dropdown__count">{matchingCount} of {tasks.length} card{tasks.length !== 1 ? 's' : ''}</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Export the filter matching function for use in useKanbanData
export { matchesDueDateFilter as matchesDueDateFilterFn };

export default KanbanFilterDropdown;
